
// Global Keyboard Input Hook
// Centralizes keyboard handling for the game, respecting UI focus states

import { useEffect, useCallback, useState } from 'react';
import { useInteraction } from '../contexts/InteractionContext';
import { useChat } from '../contexts/ChatContext';
import { useWindows } from '../framework/WindowManagerContext';
import { windowBridge } from '../framework/windowBridge';
import { KeyBindingManager } from '@/game/services/KeyBindingManager';
import { EventBus } from '@/shared/EventBus';
import { ServiceRegistry } from '@/game/core/ServiceRegistry';
import { getBoundAbility } from '@/game/systems/input/abilityTestHotkeys';
import { useColyseusMessages } from './useColyseusMessages';
import { isMainGameDomain } from '@/config/environment/environment';
import { getGmEventState } from '@/game/services/GmEventService';
import { isLeavingWorld } from '@/game/services/worldExit';
import { gameMenuStore } from '../HUD/gameMenu/gameMenuStore';
import { keyBindingsNavigationStore } from '../Windows/KeyBindings/keyBindingsNavigationStore';
import { questTrackerGuidanceEscape } from '../HUD/questTrackerGuidanceEscape';
import { settingsNavigationStore } from '../Windows/Settings/settingsNavigationStore';

interface UseGlobalInputOptions {
  onAttack?: (type: 'basic' | 'special') => void;
  onJump?: () => void;
}

export const useGlobalInput = (_options: UseGlobalInputOptions = {}) => {
  // Chat-focus + interaction surfaces moved off UIContext (Phase 3 split).
  const { isChatFocused, closeAllInteractions, shopState, questOfferState, interactingNPC } = useInteraction();

  // Diagnostic panels (F3 test rig / F4 target lifecycle) are unified-engine
  // windows now — toggle via the imperative bridge, close-all via the engine.
  const { closeAll: closeAllWindowsEngine, windows, widgets } = useWindows();

  const { togglePause, focusInput } = useChat();

  // L1.2 — local death state. Drops game-key input while we're in the
  // ghost window. Subscribes to the same net messages DeathOverlay
  // reads, scoped to local sessionId. Function-keys + chat focus still
  // pass through (chat works while dead, F-keys open debug panels).
  const [isLocalDead, setIsLocalDead] = useState(false);
  useColyseusMessages({
    playerDied: (msg: any) => {
      const mySid = (globalThis as any).nm?.getRoom?.()?.sessionId;
      if (msg?.victimSid === mySid) setIsLocalDead(true);
    },
    playerRespawned: (msg: any) => {
      const mySid = (globalThis as any).nm?.getRoom?.()?.sessionId;
      if (msg?.victimSid === mySid) setIsLocalDead(false);
    },
  });

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    // If an input, textarea, or contentEditable element is focused, don't capture game hotkeys
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' || 
      target.tagName === 'TEXTAREA' || 
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      return;
    }

    const key = event.key;
    const action = KeyBindingManager.getInstance().getActionForKey(key);

    // Escape — MMORPG-standard smart Esc (DJ ruling 2026-06-12): close
    // whatever is open first; when there is NOTHING to close (no windows, no
    // NPC interaction, no target to clear), open the game menu (stream note
    // 47: Resume, Settings, Character select, Log out). Target-clearing is
    // InputSystem's Escape job — if a target exists we let that win and don't
    // pop the menu on the same press. An open menu is the first thing Esc
    // closes, and while it is open the other UI hotkeys stay quiet.
    if (gameMenuStore.isOpen()) {
      if (key === 'Escape' && !isLeavingWorld()) gameMenuStore.close();
      return;
    }
    if (key === 'Escape') {
      if (keyBindingsNavigationStore.isEscapeIntercepted()) {
        return;
      }
      // The tracker's "where to go" panel is a HUD popover, not a window: it closes first.
      if (questTrackerGuidanceEscape.closeIfOpen()) return;
      if (widgets.keyBindings?.enabled) {
        const origin = keyBindingsNavigationStore.getOrigin();
        if (origin === 'gameMenu') {
          closeAllWindowsEngine();
          keyBindingsNavigationStore.returnToGameMenu();
          return;
        }
        if (origin === 'settings' && widgets.settings?.enabled) {
          windowBridge.close('keyBindings');
          keyBindingsNavigationStore.restoreFocusOnClose('settings');
          return;
        }
        keyBindingsNavigationStore.restoreFocusOnClose(origin);
      }
      if (widgets.settings?.enabled) {
        const origin = settingsNavigationStore.getOrigin();
        if (origin === 'gameMenu') {
          closeAllWindowsEngine();
          settingsNavigationStore.returnToGameMenu();
          return;
        }
        settingsNavigationStore.restoreFocusOnClose(origin);
      }
      const anyWindowOpen = windows.some((w) => widgets[w.id]?.enabled);
      const anyInteraction =
        shopState.isOpen || questOfferState.isOpen || !!interactingNPC;
      const hasTarget = !!ServiceRegistry.getInstance()
        .get('TargetingSystem', { optional: true })
        ?.getTarget?.();
      closeAllWindowsEngine();
      closeAllInteractions();
      if (!anyWindowOpen && !anyInteraction && !hasTarget) {
        gameMenuStore.open();
      }
      return;
    }

    // F2 - Toggle DevTools panel (migrated to the unified window engine, Phase 2)
    // Gated off on the main game domain — dev/diagnostic keybinds don't belong
    // on the public surface. F8 (chat pause) below is a legit user feature and
    // stays ungated.
    if (key === 'F2') {
      if (isMainGameDomain()) return;
      event.preventDefault();
      windowBridge.toggle('devTools');
      return;
    }

    // F3 - Toggle Ability Test Rig (active driver panel)
    if (key === 'F3') {
      if (isMainGameDomain()) return;
      event.preventDefault();
      windowBridge.toggle('abilityTestRig');
      return;
    }

    // F4 - Toggle Target Lifecycle Monitor (mob attack diagnostics)
    if (key === 'F4') {
      if (isMainGameDomain()) return;
      event.preventDefault();
      windowBridge.toggle('targetLifecycle');
      return;
    }

    // F8 - Toggle chat pause
    if (key === 'F8') {
      event.preventDefault();
      togglePause();
      return;
    }

    // F9 - Force snap to schema position (debug)
    if (key === 'F9') {
      if (isMainGameDomain()) return;
      event.preventDefault();
      EventBus.getInstance().emit('debug:forceSnapToSchema');
      return;
    }

    // Numpad 1-9 — Ability Test Rig hotkey slots. Bindings live in
    // localStorage (managed by the rig UI). Fires only if a binding
    // exists for the pressed code; otherwise falls through.
    if (event.code.startsWith('Numpad') && event.code.length === 7) {
      const bound = getBoundAbility(event.code);
      if (bound) {
        event.preventDefault();
        try {
          const reg = ServiceRegistry.getInstance();
          const es = reg.get('EntitySystem', { optional: true });
          es?.getPlayerManager?.()?.performAbility(bound);
        } catch {
          // best-effort — registry not ready yet, etc.
        }
        return;
      }
    }

    // Enter - Focus chat when not already focused. A focused button, tab or link keeps its Enter:
    // this handler's preventDefault used to cancel every keyboard press of a button (E3, 2026-11-03).
    if (key === 'Enter' && !isChatFocused && !target.closest?.('button, a[href], [role="button"], [role="tab"], [role="link"]')) {
      event.preventDefault();
      focusInput();
      return;
    }

    // If chat is focused, ignore most hotkeys
    if (isChatFocused) {
      return;
    }

    // L1.2 — drop game-action keys while in the ghost window. F-keys,
    // Escape, Enter, Shift+T (debug toggles + chat focus) already
    // returned above, so this only kills inventory/quest/skills/etc.
    if (isLocalDead) {
      return;
    }

    if (!action) return;

    switch (action) {
    case 'inventory':
      // Inventory migrated to the unified window engine (Phase 2) — toggle via the bridge.
      windowBridge.toggle('inventory');
      break;
    case 'quest_log':
      // Quest Log migrated to the unified window engine (Phase 2) — toggle via the bridge.
      windowBridge.toggle('quests');
      break;
    case 'character_sheet':
      // C opens the unified Equipment + Stats Character sheet.
      windowBridge.toggle('equipment');
      break;
    case 'talents':
      // N opens the server-authoritative Talent Tree.
      windowBridge.toggle('talents');
      break;
    case 'skills':
      // Spellbook migrated to the unified window engine (Phase 2) — toggle via the bridge.
      windowBridge.toggle('spells');
      break;
    case 'party':
      // Party migrated to the unified window engine (Phase 2) — toggle via the bridge.
      windowBridge.toggle('party');
      break;
    case 'map':
    case 'world_map':
      // M — World Map (lane AK plan 22; on M since lane AN, v2.6 bindings).
      // `map` is the retired Map Picker action: a player who rebound it to
      // another key gets the World Map there. The Map Picker is a dev-surface
      // window now (🛠 launcher / DEV BAR), never a player key.
      windowBridge.toggle('worldMap');
      break;
    case 'leaderboard':
      // O — Leaderboard (lane BM, plan 23 row 40): top characters + online now.
      windowBridge.toggle('leaderboard');
      break;
    case 'trade':
      windowBridge.toggle('trade');
      break;
    case 'bank':
      windowBridge.toggle('bank');
      break;
    case 'crafting':
      windowBridge.toggle('crafting');
      break;
    case 'key_bindings':
      keyBindingsNavigationStore.toggleDirect();
      break;
    case 'interact':
      // Interaction logic is usually game-loop side (InputSystem),
      // but sometimes UI needs to know?
      // For now, InputSystem handles 'E' for interaction.
      break;
    case 'fishing_cast':
    case 'fishing_reel': {
      // GM Event fishing — context-sensitive: check the current fishing
      // state and send the appropriate message. Both actions are bound to
      // the same key (F); the action string tells us which the player
      // "intends", but we override based on actual state.
      const room = (window as any).nm?.getRoom?.();
      if (!room) break;
      const gmState = getGmEventState();
      if (!gmState?.announcement || gmState.announcement.phase !== 'live') break;
      if (!gmState?.fishingPlayerState) break;
      const localSid = room.sessionId;
      if (gmState.fishingPlayerState.sessionId !== localSid) break;
      const status = gmState.fishingPlayerState.status;
      const eventId = gmState.announcement.eventId;
      if (status === 'bite') {
        room.send('gmEventFishingReel', { eventId });
      } else if (status === 'idle' || status === 'caught' || status === 'missed') {
        room.send('gmEventFishingCast', { eventId });
      }
      break;
    }

        // Setup other toggles if we add bindings for them
    }
  }, [
    isChatFocused,
    closeAllWindowsEngine, closeAllInteractions,
    windows, widgets, shopState.isOpen, questOfferState.isOpen, interactingNPC,
    togglePause, focusInput, isLocalDead
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);
};
